import { type JourneyRequest, objectIdSchema, type TouchRequest } from "@ternary/contracts"
import { patchTouchConsent, postJourney, postTouch } from "../api.js"

/** The visitor's answer to the cookie banner. `ads` is the only consent flag. */
export type ConsentChoice = { ads: boolean; at: string }

/** localStorage key of the consent choice; analytics.ts watches it for a decline in another tab. */
export const CONSENT_KEY = "tny_consent"
const TOUCH_KEY = "tny_touch_id"
const FIRED_KEY = "tny_fired"
const LANDING_KEY = "tny_landing"
// A choice older than this reads as unanswered, so the banner asks again.
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1_000

// Storage can be missing or throw (blocked cookies, some private modes). Tracking must never break
// the page, so every read and write goes through these.
function readStorage(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key)
  } catch {
    return null
  }
}

function writeStorage(storage: () => Storage, key: string, value: string) {
  try {
    storage().setItem(key, value)
  } catch {
    // Not remembered: the banner asks again, and tracking fails closed.
  }
}

function removeStorage(storage: () => Storage, key: string) {
  try {
    storage().removeItem(key)
  } catch {
    // Storage is blocked, so nothing was kept.
  }
}

const local = () => window.localStorage
const session = () => window.sessionStorage

// The touch being captured, so concurrent calls share one POST.
let pendingTouch: Promise<string | undefined> | null = null

function storedTouchId(): string | undefined {
  const stored = readStorage(session, TOUCH_KEY)
  return objectIdSchema.safeParse(stored).success ? (stored ?? undefined) : undefined
}

/**
 * The stored choice (v1 JSON in localStorage "tny_consent"), or null when missing, malformed or
 * older than 365 days.
 */
export function readConsent(): ConsentChoice | null {
  try {
    const stored: unknown = JSON.parse(readStorage(local, CONSENT_KEY) ?? "null")
    if (typeof stored !== "object" || stored === null) return null
    const { ads, at, v } = stored as Record<string, unknown>
    if (v !== 1 || typeof ads !== "boolean" || typeof at !== "string") return null
    const age = Date.now() - Date.parse(at)
    if (!(age <= CONSENT_MAX_AGE_MS)) return null
    return { ads, at }
  } catch {
    return null
  }
}

/**
 * Stores the choice and tells the server. With no touch yet, the remembered landing's touch posts
 * now, carrying this choice (R54). When the session already has a touch, or one is being captured,
 * the change goes through PATCH …/consent instead: `ads: false` strips the browser's identifiers.
 */
export function writeConsent(ads: boolean): ConsentChoice {
  const choice = { ads, at: new Date().toISOString() }
  writeStorage(local, CONSENT_KEY, JSON.stringify({ v: 1, ...choice }))
  const touch = pendingTouch ?? storedTouchId()
  if (touch) {
    void Promise.resolve(touch)
      .then((touchId) => (touchId ? patchTouchConsent(touchId, { ads }) : undefined))
      .catch(() => undefined)
  } else {
    void ensureTouch()
  }
  return choice
}

const UTM_KEYS = ["campaign", "content", "medium", "source", "term"] as const
const CLICK_ID_KEYS = ["fbclid", "gbraid", "gclid", "ttclid", "wbraid"] as const
const COOKIE_NAMES = { fbc: "_fbc", fbp: "_fbp", ttp: "_ttp" } as const

/**
 * The values that are present, trimmed. A value longer than the contract allows is dropped, so one
 * odd parameter never costs the whole touch a 400.
 */
function present<Key extends string>(
  pairs: ReadonlyArray<readonly [Key, string | null | undefined]>,
  max: number,
): Partial<Record<Key, string>> {
  const values: Partial<Record<Key, string>> = {}
  for (const [key, raw] of pairs) {
    const value = raw?.trim()
    if (value && value.length <= max) values[key] = value
  }
  return values
}

function cookieValue(cookie: string, name: string): string | undefined {
  for (const part of cookie.split(";")) {
    const separator = part.indexOf("=")
    if (separator < 0 || part.slice(0, separator).trim() !== name) continue
    const value = part.slice(separator + 1)
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }
  return undefined
}

/**
 * The `POST /api/v1/touch` body for this landing. Click ids and the vendors' cookies are sent
 * whatever the consent: the server derives the channel from them, and without `consent.ads` it
 * stores none of them.
 */
export function buildTouchPayload(
  url: URL,
  referrer: string,
  cookie: string,
  consentAds: boolean,
): TouchRequest {
  const params = url.searchParams
  const landingUrl = url.href.length <= 2_048 ? url.href : `${url.origin}${url.pathname}`
  const { referrer: knownReferrer } = present([["referrer", referrer]], 2_048)
  return {
    clickIds: present(
      CLICK_ID_KEYS.map((key) => [key, params.get(key)] as const),
      500,
    ),
    consent: { ads: consentAds },
    cookies: present(
      Object.entries(COOKIE_NAMES).map(
        ([key, name]) => [key as keyof typeof COOKIE_NAMES, cookieValue(cookie, name)] as const,
      ),
      500,
    ),
    landingUrl,
    ...(knownReferrer ? { referrer: knownReferrer } : {}),
    utm: present(
      UTM_KEYS.map((key) => [key, params.get(`utm_${key}`)] as const),
      200,
    ),
  }
}

/**
 * Records the landing once per browser session (sessionStorage "tny_touch_id") and resolves its
 * touch id, which the page sends with its journey events. A failure resolves undefined: tracking
 * never blocks signup.
 */
export function captureTouch(payload: TouchRequest): Promise<string | undefined> {
  const stored = storedTouchId()
  if (stored) return Promise.resolve(stored)
  pendingTouch ??= postTouch(payload)
    .then(({ touchId }) => {
      writeStorage(session, TOUCH_KEY, touchId)
      // Recorded: the click ids no longer need to stay in the browser. A failure keeps them, so a
      // later call can still record the touch.
      removeStorage(session, LANDING_KEY)
      return touchId
    })
    .catch(() => undefined)
    .finally(() => {
      pendingTouch = null
    })
  return pendingTouch
}

/**
 * Keeps this session's first landing (sessionStorage "tny_landing") at page load. The touch is
 * posted later, once the visitor answers the banner or starts signing up, so a consenting visitor's
 * click ids are stored (R54). A later page in the session keeps the first landing's UTMs.
 */
export function rememberLanding(url: URL, referrer: string): void {
  // Once the touch is recorded, nothing about a later page is kept: its click ids would sit in
  // sessionStorage with no touch ever posting them (R67).
  if (storedTouchId() || readStorage(session, LANDING_KEY) !== null) return
  writeStorage(session, LANDING_KEY, JSON.stringify({ referrer, url: url.href }))
}

function rememberedLanding(): { referrer: string; url: URL } | null {
  try {
    const stored: unknown = JSON.parse(readStorage(session, LANDING_KEY) ?? "null")
    if (typeof stored !== "object" || stored === null) return null
    const { referrer, url } = stored as Record<string, unknown>
    if (typeof referrer !== "string" || typeof url !== "string") return null
    return { referrer, url: new URL(url) }
  } catch {
    return null
  }
}

/**
 * The session's touch id. With no touch yet, it posts the remembered landing, carrying the stored
 * choice or `ads: false` when the visitor has not answered. The signup page awaits it before
 * `POST …/start`, because the server links a signup to its touch through the cookie this sets.
 * Concurrent calls share one POST; with no landing remembered it resolves undefined.
 */
export function ensureTouch(): Promise<string | undefined> {
  const stored = storedTouchId()
  if (stored) return Promise.resolve(stored)
  if (pendingTouch) return pendingTouch
  const landing = rememberedLanding()
  if (!landing) return Promise.resolve(undefined)
  return captureTouch(
    buildTouchPayload(landing.url, landing.referrer, document.cookie, readConsent()?.ads ?? false),
  )
}

/**
 * Sends a journey event as JSON with `fetch` keepalive. Errors are swallowed; the promise settles
 * once the request was answered or failed, for a caller that must order a later call after it.
 */
export function sendJourney(body: JourneyRequest): Promise<void> {
  return postJourney(body).catch(() => undefined)
}

function firedEventIds(): string[] {
  try {
    const stored: unknown = JSON.parse(readStorage(local, FIRED_KEY) ?? "[]")
    return Array.isArray(stored) ? stored.filter((id): id is string => typeof id === "string") : []
  } catch {
    return []
  }
}

/**
 * True the first time `eventId` is marked on this device (localStorage "tny_fired"), else false,
 * so `Lead` and `CompleteRegistration` fire at most once per signup across reloads and revisits.
 * A mark that cannot be stored is false too: Google does not deduplicate on the event id, so an
 * unstorable mark would repeat the conversion on every reload.
 */
export function markFired(eventId: string): boolean {
  const fired = firedEventIds()
  if (fired.includes(eventId)) return false
  writeStorage(local, FIRED_KEY, JSON.stringify([...fired, eventId]))
  return firedEventIds().includes(eventId)
}
